import { ReactNode, useCallback, useMemo, useState } from "react";
import { CarouselContext } from "./carousel.context";

export type CarouselProps = {
  children: ReactNode;
};

export function CarouselProvider({ children }: CarouselProps) {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [slides, setSlides] = useState<string[]>([]);
  const [index, setIndex] = useState<number>(0);
  const [hover, setHover] = useState<boolean>(false);

  /**
   * Total slides on carousel
   *
   */
  const slidesCount = useMemo(() => {
    return slides.length;
  }, [slides]);

  /**
   * Check if we can go back
   *
   */
  const canGoBack = useMemo(() => {
    return index > 0;
  }, [index]);

  /**
   * Check if we can go forward
   *
   */
  const canGoForward = useMemo(() => {
    return slidesCount - 1 > index;
  }, [slidesCount, index]);

  /**
   * Goes to the next slide
   *
   */
  const goForward = useCallback(() => {
    if (canGoForward) {
      setIndex((index) => index + 1);
    }
  }, [canGoForward, setIndex]);

  /**
   * Goes to the previous slide
   *
   */
  const goBack = useCallback(() => {
    if (canGoBack) {
      setIndex((index) => index - 1);
    }
  }, [canGoBack, setIndex]);

  /**
   * Goes to a specific slide
   *
   */
  const goTo = useCallback((targetIndex: number) => {
    if (canGoForward || canGoBack) {
      setIndex(targetIndex);
    }
  }, [canGoForward, canGoBack, setIndex])

  /**
   * Current scroll offset
   *
   */
  const scrollOffset = useMemo(() => {
    return index * containerWidth;
  }, [index, containerWidth]);

  return (
    <CarouselContext.Provider
      value={{
        slides,
        setSlides,
        slidesCount,
        containerWidth,
        setContainerWidth,
        index,
        setIndex,
        canGoBack,
        canGoForward,
        goForward,
        goBack,
        scrollOffset,
        hover,
        setHover,
        goTo,
      }}
    >
      {children}
    </CarouselContext.Provider>
  );
}
